import type { ReactNode } from "react";
import { useEffect } from "react";

/**
 * The frame the Terms of Service and the Privacy Policy share: the title, when the text last
 * changed, and the prose styling, applied from the wrapper so the documents stay plain
 * semantic HTML. A `#fragment` in the URL scrolls to that section once the page has mounted,
 * the way a link into the middle of a policy expects.
 */
export function PolicyDocument({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}) {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <article className="mx-auto max-w-3xl text-sm leading-relaxed text-foreground [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_h2]:mt-8 [&_h2]:scroll-mt-20 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:tracking-tight [&_h3]:mt-6 [&_h3]:font-semibold [&_li]:mt-1 [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mt-3 [&_section]:scroll-mt-20 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-1 text-xs text-muted-foreground">
        <em>Last updated</em>: {lastUpdated}
      </p>
      {children}
    </article>
  );
}

/** A numbered section of a document; `id` makes it a link target. */
export function PolicySection({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number?: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id}>
      <h2>
        {number !== undefined ? `${number}. ` : ""}
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A lettered clause inside a section: "(a) Title. Body". */
export function PolicyClause({
  label,
  title,
  children,
}: {
  label: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <p>
      {label}
      {title ? (
        <>
          {" "}
          <u>{title}</u>.
        </>
      ) : null}{" "}
      {children}
    </p>
  );
}
